import { useQueries } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import { gridQuery, isNoGrid } from "./api";
import { mergeIssues } from "./feed_issues";
import { FeedIssuesNote } from "./FeedIssuesNote";
import { COPY, humanDate, MARKET_LABEL, stamp } from "./format";
import { HighlightsSection } from "./HighlightsSection";
import { KPI_COPY, MARKETS } from "./kpi";
import { MarginHealthSection } from "./MarginHealthSection";
import { WEEKLY_COPY } from "./margin_weeks";
import { SectionHeading, WeeklyMovesSection } from "./WeeklyMovesSection";

const P = KPI_COPY.page;

/** The Overview tab (R5, second slice): Margin health and Highlights live from the KPI read, the two release cards, then Weekly margin moves.
 * C1 v1.34: above the sections, the page names the weekly Xero stock read, says when no market has a grid yet, and names every
 * late grid and feed. The three grid reads are the ones the sections already hold (one cache entry each); none of these lines
 * hides or waits on a section. */
export function OverviewTab({ areaName }: { areaName: string }) {
  const grids = useQueries({ queries: MARKETS.map((m) => gridQuery(m)) });
  const envelopes = grids.map((g) => g.data?.envelope ?? null);
  // The feeds are one store-wide read, so any market's envelope carries them: the first one read.
  const feeds = envelopes.find((e) => e != null)?.feeds;
  // The Xero stock read has its own note, top right: it is named there and not again among the late lines.
  const xero = feeds?.pool_cogs;
  const noData = grids.every((g) => g.error != null && isNoGrid(g.error));
  // C1 v1.60 (GATE-1089): C2 v1.56 names each feed issue with its last good date and why, on the one freshness rule the late
  // feed lines read, so a feed the note names is not named again among those lines (a feed no grid figure reads, the KPI sheets,
  // keeps its line).
  const issues = mergeIssues(...envelopes.map((e) => e?.feed_issues));
  const named = new Set(issues.map((i) => i.feed));
  const late = [
    ...envelopes.flatMap((e) => (e?.stale ? [P.staleGrid(MARKET_LABEL[e.market], stamp(e.computed_at))] : [])),
    ...Object.entries(feeds ?? {}).flatMap(([feed, entry]) => (feed !== "pool_cogs" && !named.has(feed) && entry?.stale ? [P.staleFeed(feed, entry.latest ? humanDate(entry.latest) : null)] : [])),
  ];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ks-ink">{WEEKLY_COPY.pageTitle}</h1>
          <p className="text-sm text-ks-grey-1">{WEEKLY_COPY.pageNote}</p>
        </div>
        {xero && (
          <Badge variant="outline" className={`ml-auto ${xero.latest === null || xero.stale ? "border-ks-warn text-ks-warn" : ""}`} data-page-xero>
            {xero.latest === null ? COPY.xeroNotRead : xero.stale ? COPY.xeroReadOld(humanDate(xero.latest)) : COPY.xeroRead(humanDate(xero.latest))}
          </Badge>
        )}
      </div>
      {noData && <Card className="border-dashed" data-page-empty><CardContent className="py-4 text-sm text-ks-ink">{P.empty}</CardContent></Card>}
      <FeedIssuesNote issues={issues} />
      {late.length > 0 && (
        <Alert role="status" className="border-ks-warn" data-page-late>
          <AlertDescription><ul className="flex flex-col gap-1">{late.map((line) => <li key={line}>{line}</li>)}</ul></AlertDescription>
        </Alert>
      )}
      <MarginHealthSection areaName={areaName} />
      <HighlightsSection areaName={areaName} />
      {WEEKLY_COPY.sections.map((s) => (
        <section key={s.n} className="flex flex-col gap-2">
          <SectionHeading n={s.n} title={s.title} desc={s.desc} />
          <Card className="border-dashed"><CardContent className="py-3 text-sm text-ks-grey-1">{s.later}</CardContent></Card>
        </section>
      ))}
      <WeeklyMovesSection areaName={areaName} />
    </div>
  );
}
